import { useState } from "react";
import {
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
  Trash2,
  Plus,
  Settings2,
} from "lucide-react";
import SectionFields from "@/pages/admin/SectionFields";
import ButtonsEditor from "@/pages/admin/ButtonsEditor";
import ImageUploader from "@/pages/admin/ImageUploader";
import CustomSectionEditor from "@/pages/admin/CustomSectionEditor";
import {
  SECTION_LABELS,
  SECTION_SCHEMA,
  SECTION_IMAGES,
  BUTTON_SECTIONS,
} from "@/pages/admin/schema";

function move(arr, from, to) {
  const a = arr.slice();
  if (to < 0 || to >= a.length) return a;
  const [it] = a.splice(from, 1);
  a.splice(to, 0, it);
  return a;
}

/* ------------------------------------------------------------------ *
 * Sub-components are declared at MODULE LEVEL (never inside the main   *
 * PageBuilder render). This keeps their component identity stable      *
 * across re-renders, so the inputs they contain are reconciled instead *
 * of remounted — which permanently prevents the "focus loss after one  *
 * character" bug while typing.                                         *
 * ------------------------------------------------------------------ */
function SectionRow({
  k,
  name,
  isCustom,
  reorderable,
  open,
  item,
  onToggle,
  onToggleVisible,
  onMoveUp,
  onMoveDown,
  children,
}) {
  const visible = item.visible !== false;
  return (
    <div className="border border-ink/20" data-testid={`section-row-${k}`}>
      <div className="flex items-center gap-2 p-3">
        {reorderable && (
          <div className="flex flex-col">
            <button onClick={onMoveUp} data-testid={`move-up-${k}`} className="p-0.5 hover:bg-ink/10">
              <ChevronUp size={15} />
            </button>
            <button onClick={onMoveDown} data-testid={`move-down-${k}`} className="p-0.5 hover:bg-ink/10">
              <ChevronDown size={15} />
            </button>
          </div>
        )}
        <button
          onClick={onToggle}
          data-testid={`toggle-editor-${k}`}
          className="flex-1 text-left flex items-center gap-2"
        >
          <span className="font-display text-xl">{name}</span>
          {isCustom && <span className="eyebrow text-ink/40">personalizată</span>}
        </button>
        <button
          onClick={onToggleVisible}
          data-testid={`visibility-${k}`}
          title={visible ? "Ascunde" : "Afișează"}
          className={`p-2 border ${visible ? "border-ink/20" : "border-red-700/40 text-red-700"}`}
        >
          {visible ? <Eye size={15} /> : <EyeOff size={15} />}
        </button>
        <button
          onClick={onToggle}
          className="p-2 border border-ink/20 hover:bg-ink hover:text-paper transition-colors"
        >
          <Settings2 size={15} />
        </button>
      </div>
      {open && <div className="border-t border-ink/15 bg-ink/[0.015]">{children}</div>}
    </div>
  );
}

function FixedSectionRow({ k, label, open, onToggle, children }) {
  return (
    <div className="border border-ink/20" data-testid={`section-row-${k}`}>
      <button
        onClick={onToggle}
        data-testid={`toggle-editor-${k}`}
        className="w-full flex items-center justify-between p-3 text-left"
      >
        <span className="font-display text-xl">{label}</span>
        <Settings2 size={15} />
      </button>
      {open && <div className="border-t border-ink/15 bg-ink/[0.015] p-4">{children}</div>}
    </div>
  );
}

const FIXED_BOTTOM = ["library", "contact", "footer", "nav"];

export default function PageBuilder({ draft, setDraft, lang, setLang }) {
  const [openKey, setOpenKey] = useState(null);
  const sections = draft.sections;
  const order = sections.order;
  const items = sections.items;

  const setContent = (key, obj) =>
    setDraft((d) => ({
      ...d,
      content: { ...d.content, [lang]: { ...d.content[lang], [key]: obj } },
    }));

  const setItem = (key, patch) =>
    setDraft((d) => ({
      ...d,
      sections: {
        ...d.sections,
        items: { ...d.sections.items, [key]: { ...d.sections.items[key], ...patch } },
      },
    }));

  const setItemFull = (key, obj) =>
    setDraft((d) => ({
      ...d,
      sections: { ...d.sections, items: { ...d.sections.items, [key]: obj } },
    }));

  const setOrder = (newOrder) =>
    setDraft((d) => ({ ...d, sections: { ...d.sections, order: newOrder } }));

  const setImage = (key, v) =>
    setDraft((d) => ({ ...d, images: { ...d.images, [key]: v } }));

  const setImageAt = (key, idx, v) =>
    setDraft((d) => {
      const arr = [...(d.images[key] || [])];
      arr[idx] = v;
      return { ...d, images: { ...d.images, [key]: arr } };
    });

  const addCustom = () => {
    const id = `custom-${Date.now()}`;
    setDraft((d) => ({
      ...d,
      sections: {
        ...d.sections,
        order: [...d.sections.order, id],
        items: {
          ...d.sections.items,
          [id]: {
            visible: true,
            type: "custom",
            navLabel: { ro: "Secțiune nouă", en: "New section" },
            theme: "light",
            blocks: [],
          },
        },
      },
    }));
    setOpenKey(id);
  };

  const deleteCustom = (id) => {
    if (!window.confirm("Ștergi această secțiune?")) return;
    setDraft((d) => {
      const it = { ...d.sections.items };
      delete it[id];
      return {
        ...d,
        sections: { ...d.sections, order: d.sections.order.filter((k) => k !== id), items: it },
      };
    });
  };

  // Plain helper that returns JSX (NOT used as a component) — safe to keep inside.
  const renderEditor = (key) => {
    const item = items[key] || {};
    if (item.type === "custom") {
      return (
        <div className="p-4 space-y-4">
          <CustomSectionEditor item={item} onChange={(obj) => setItemFull(key, obj)} lang={lang} />
          <button
            onClick={() => deleteCustom(key)}
            className="eyebrow inline-flex items-center gap-2 border border-red-700 text-red-700 px-4 py-2 hover:bg-red-700 hover:text-paper transition-colors"
          >
            <Trash2 size={13} /> Șterge secțiunea
          </button>
        </div>
      );
    }
    const schema = SECTION_SCHEMA[key];
    return (
      <div className="p-4 space-y-5">
        {schema && (
          <SectionFields
            fields={schema}
            value={draft.content[lang]?.[key] || {}}
            onChange={(obj) => setContent(key, obj)}
            testidPrefix={`${key}.`}
          />
        )}
        {BUTTON_SECTIONS.has(key) && (
          <ButtonsEditor
            buttons={item.buttons || []}
            onChange={(b) => setItem(key, { buttons: b })}
            lang={lang}
          />
        )}
        {SECTION_IMAGES[key] && (
          <div className="space-y-3">
            <div className="eyebrow text-ink/60">Imagini</div>
            {SECTION_IMAGES[key].map((img) =>
              img.array ? (
                <div key={img.key} className="grid sm:grid-cols-2 gap-3">
                  {Array.from({ length: img.array }).map((_, idx) => (
                    <ImageUploader
                      key={idx}
                      label={`${img.label} #${idx + 1}`}
                      value={draft.images[img.key]?.[idx] || ""}
                      onChange={(v) => setImageAt(img.key, idx, v)}
                      testid={`img-${img.key}-${idx}`}
                    />
                  ))}
                </div>
              ) : (
                <ImageUploader
                  key={img.key}
                  label={img.label}
                  value={draft.images[img.key] || ""}
                  onChange={(v) => setImage(img.key, v)}
                  testid={`img-${img.key}`}
                />
              )
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div className="eyebrow text-ink/50">Editezi conținut în limba:</div>
        <div className="flex items-center gap-1 eyebrow border border-ink/25">
          <button
            onClick={() => setLang("ro")}
            data-testid="content-lang-ro"
            className={`px-4 py-2 ${lang === "ro" ? "bg-ink text-paper" : ""}`}
          >
            RO
          </button>
          <button
            onClick={() => setLang("en")}
            data-testid="content-lang-en"
            className={`px-4 py-2 ${lang === "en" ? "bg-ink text-paper" : ""}`}
          >
            EN
          </button>
        </div>
      </div>

      <div className="eyebrow text-ink/40 mb-2">Secțiune fixă (sus)</div>
      <div className="space-y-2 mb-8">
        <SectionRow
          k="hero"
          name={SECTION_LABELS.hero}
          isCustom={false}
          reorderable={false}
          open={openKey === "hero"}
          item={items.hero || {}}
          onToggle={() => setOpenKey(openKey === "hero" ? null : "hero")}
          onToggleVisible={() => setItem("hero", { visible: !((items.hero || {}).visible !== false) })}
        >
          {openKey === "hero" ? renderEditor("hero") : null}
        </SectionRow>
      </div>

      <div className="eyebrow text-ink/40 mb-2">Secțiuni (reordonabile)</div>
      <div className="space-y-2">
        {order.map((k, i) => {
          const item = items[k] || {};
          const isCustom = item.type === "custom";
          const name = isCustom
            ? item.navLabel?.[lang] || item.navLabel?.ro || "Secțiune"
            : SECTION_LABELS[k] || k;
          const open = openKey === k;
          return (
            <SectionRow
              key={k}
              k={k}
              name={name}
              isCustom={isCustom}
              reorderable
              open={open}
              item={item}
              onToggle={() => setOpenKey(open ? null : k)}
              onToggleVisible={() => setItem(k, { visible: !(item.visible !== false) })}
              onMoveUp={() => setOrder(move(order, i, i - 1))}
              onMoveDown={() => setOrder(move(order, i, i + 1))}
            >
              {open ? renderEditor(k) : null}
            </SectionRow>
          );
        })}
      </div>

      <button
        onClick={addCustom}
        data-testid="add-section"
        className="mt-4 eyebrow inline-flex items-center gap-2 border border-ink px-5 py-3 hover:bg-ink hover:text-paper transition-colors"
      >
        <Plus size={14} /> Adaugă secțiune nouă
      </button>

      <div className="eyebrow text-ink/40 mb-2 mt-10">Secțiuni fixe (jos)</div>
      <div className="space-y-2">
        {FIXED_BOTTOM.map((k) => (
          <FixedSectionRow
            key={k}
            k={k}
            label={SECTION_LABELS[k]}
            open={openKey === k}
            onToggle={() => setOpenKey(openKey === k ? null : k)}
          >
            {openKey === k ? (
              <SectionFields
                fields={SECTION_SCHEMA[k]}
                value={draft.content[lang]?.[k] || {}}
                onChange={(obj) => setContent(k, obj)}
                testidPrefix={`${k}.`}
              />
            ) : null}
          </FixedSectionRow>
        ))}
      </div>
    </div>
  );
}
